import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useCrm, crmStore } from '../store';
import { Card, PageTitle, StatutBadge, Tag } from '../ui';
import { formatEUR, formatDateFR, daysUntil, jLabel } from '../utils';
import { Search, Truck, Pencil, AlertTriangle, X, Sparkles, Activity, Trash2, FileSpreadsheet, Users2 } from 'lucide-react';
import { exportXlsx } from '../utils/exportXlsx';
import PrestationForm from '../components/PrestationForm';
import IntervenantsModal from '../components/IntervenantsModal';
import { useAuth } from '../auth/AuthContext';
import { useConfirm } from '../components/ConfirmDialog';
import { StructureBadge } from '../components/StructureSelector';
import { useStructureFilter } from '../structureContext';
import { toast } from 'sonner';
import { type Prestation, STATUTS, STATUT_COLORS } from '../types';

function StatutInlineSelect({ p }: { p: Prestation }) {
  const [val, setVal] = useState(p.statut);
  useEffect(() => { setVal(p.statut); }, [p.statut]);
  const cls = STATUT_COLORS[val] || 'bg-slate-500/15 text-slate-300 border-slate-500/30';
  return (
    <select
      value={val}
      onChange={async (e) => {
        const next = e.target.value;
        setVal(next);
        try {
          await crmStore.upsertPrest({ ...p, statut: next });
          toast.success(`Statut → ${next}`);
        } catch (err: any) {
          setVal(p.statut);
          toast.error(err?.message || 'Erreur mise à jour statut');
        }
      }}
      className={`text-[10px] px-1.5 py-0.5 rounded border ${cls} bg-slate-900/40 cursor-pointer focus:outline-none focus:ring-1 focus:ring-amber-500/40 max-w-[140px]`}
      onClick={(e) => e.stopPropagation()}
    >
      {STATUTS.map((s) => <option key={s} value={s} className="bg-slate-900 text-slate-100">{s}</option>)}
    </select>
  );
}

/**
 * Parse "12 Rue Voltaire, 51100 Reims, France" → { ville: "Reims", cpDept: "51100 · 51" }
 * Tente plusieurs heuristiques pour extraire ville + code postal/département.
 */
function parseLieu(lieu: string | null | undefined): { ville: string; cpDept: string } {
  if (!lieu) return { ville: '', cpDept: '' };
  // Cas formaté Nominatim : "..., 51100 Reims, ..., France"
  const cpMatch = lieu.match(/(\d{4,5})\s+([A-Za-zÀ-ÿ' \-]+)/);
  if (cpMatch) {
    const cp = cpMatch[1];
    const ville = cpMatch[2].trim().split(',')[0];
    const dept = cp.slice(0, cp.length === 5 ? 2 : 1);
    return { ville, cpDept: `${cp} · ${dept}` };
  }
  // Cas "Bar-sur-Seine (10) – Aube"
  const parenMatch = lieu.match(/^([^(]+)\((\d{1,3})\)/);
  if (parenMatch) {
    return { ville: parenMatch[1].trim(), cpDept: parenMatch[2] };
  }
  // Fallback : première partie avant virgule
  const ville = lieu.split(',')[0].trim();
  return { ville, cpDept: '' };
}

function daysSinceUpdate(p: Prestation): number | null {
  const ref = p.updatedAt || p.dateCreation;
  if (!ref) return null;
  const ms = Date.now() - new Date(ref).getTime();
  return Math.max(0, Math.floor(ms / (1000 * 60 * 60 * 24)));
}

const FILTERS = [
  { key: 'all', label: 'Tous' },
  { key: 'signe', label: '✅ Signés', match: (s: string) => s === 'Signé' },
  { key: 'propal', label: '📝 Propal', match: (s: string) => /propal/i.test(s) },
  { key: 'relance', label: '🔔 Relances', match: (s: string) => /relance/i.test(s) },
  { key: 'refus', label: '❌ Refusés', match: (s: string) => /refus/i.test(s) },
];

export default function Prestations() {
  const data = useCrm();
  const confirm = useConfirm();
  const { matches: matchesStructure } = useStructureFilter();
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all');
  const [year, setYear] = useState<string>('all');
  const [showArchived, setShowArchived] = useState(false);
  const [editing, setEditing] = useState<Prestation | null>(null);
  const [openForm, setOpenForm] = useState(false);
  const { hasRole } = useAuth();
  const canAssign = hasRole('admin') || hasRole('direction');
  const [intervPrest, setIntervPrest] = useState<Prestation | null>(null);

  // Filtres avancés (CDC §5.2 + §6.2)
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [domaine, setDomaine] = useState('');
  const [service, setService] = useState('');
  const [artiste, setArtiste] = useState('');
  const [lieu, setLieu] = useState('');
  const [montantMin, setMontantMin] = useState('');
  const [montantMax, setMontantMax] = useState('');

  const navigate = useNavigate();
  const openNew = () => navigate('/crm-pro-evenementiel/nouvelle-demande');
  const openEdit = (p: Prestation) => navigate(`/crm-pro-evenementiel/nouvelle-demande?edit=${encodeURIComponent(p.id)}`);

  // Ouvre automatiquement l'édition via ?id= (numéro métier OU UUID DB)
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    const id = searchParams.get('id');
    if (!id || !data.prest.length) return;
    let p = data.prest.find((x) => x.id === id);
    if (!p) {
      // Fallback : si on reçoit un UUID DB, on le résout vers le numéro métier
      (async () => {
        const { supabase } = await import('@/integrations/supabase/client');
        const { data: row } = await supabase.from('prestations').select('numero').eq('id', id).maybeSingle();
        if (row?.numero) {
          const found = data.prest.find((x) => x.id === row.numero);
          if (found) {
            setEditing(found);
            setOpenForm(true);
          }
        }
        searchParams.delete('id');
        setSearchParams(searchParams, { replace: true });
      })();
      return;
    }
    setEditing(p);
    setOpenForm(true);
    searchParams.delete('id');
    setSearchParams(searchParams, { replace: true });
  }, [searchParams, data.prest, setSearchParams]);

  const years = useMemo(() => {
    const ys = new Set(data.prest.map((p) => p.dateEvent?.slice(0, 4)).filter(Boolean));
    return Array.from(ys).sort();
  }, [data.prest]);

  // Détection des conflits de date (≥2 prestations le même jour, hors refus) — CDC §5.2
  const conflits = useMemo(() => {
    const byDate = new Map<string, number>();
    for (const p of data.prest) {
      if (!p.dateEvent || /Refus/i.test(p.statut)) continue;
      byDate.set(p.dateEvent, (byDate.get(p.dateEvent) ?? 0) + 1);
    }
    const set = new Set<string>();
    byDate.forEach((n, d) => { if (n >= 2) set.add(d); });
    return set;
  }, [data.prest]);

  // Listes pour selects filtres avancés
  const allDomaines = useMemo(() => Array.from(new Set(data.prest.flatMap((p) => p.domaines))).sort(), [data.prest]);
  const allServices = useMemo(() => Array.from(new Set(data.prest.flatMap((p) => p.services))).sort(), [data.prest]);
  const allArtistes = useMemo(() => Array.from(new Set(data.prest.flatMap((p) => p.artistes))).sort(), [data.prest]);

  const filtered = useMemo(() => {
    return data.prest.filter((p) => {
      // Filtre global structure (Cygnature / Suivez Vos Rêves)
      if (!matchesStructure(p.structure)) return false;
      // Mode archives : affiche UNIQUEMENT les dossiers archivés ET dont la date est passée.
      // Sinon, masque les archives.
      const todayStr = new Date().toISOString().slice(0, 10);
      const isPast = !!p.dateEvent && p.dateEvent < todayStr;
      if (showArchived) {
        if (!p.archivedAt) return false;
        if (!isPast) return false;
      } else {
        if (p.archivedAt && isPast) return false;
      }
      if (year !== 'all' && !p.dateEvent.startsWith(year)) return false;
      const f = FILTERS.find((x) => x.key === filter);
      if (f?.match && !f.match(p.statut)) return false;
      if (search) {
        const q = search.toLowerCase();
        if (![p.client, p.lieu, p.libelle, ...(p.themes || []), p.statut, ...p.artistes].join(' ').toLowerCase().includes(q)) return false;
      }
      // Filtres avancés
      if (dateFrom && (!p.dateEvent || p.dateEvent < dateFrom)) return false;
      if (dateTo && (!p.dateEvent || p.dateEvent > dateTo)) return false;
      if (domaine && !p.domaines.includes(domaine)) return false;
      if (service && !p.services.includes(service)) return false;
      if (artiste && !p.artistes.includes(artiste)) return false;
      if (lieu && !(p.lieu || '').toLowerCase().includes(lieu.toLowerCase())) return false;
      if (montantMin && (p.propTTC ?? 0) < Number(montantMin)) return false;
      if (montantMax && (p.propTTC ?? 0) > Number(montantMax)) return false;
      return true;
    }).sort((a, b) => {
      // Tri automatique par date d'événement (plus proche en premier, sans date à la fin)
      const da = a.dateEvent || '';
      const db = b.dateEvent || '';
      if (!da && !db) return 0;
      if (!da) return 1;
      if (!db) return -1;
      return da.localeCompare(db);
    });
  }, [data.prest, search, filter, year, showArchived, dateFrom, dateTo, domaine, service, artiste, lieu, montantMin, montantMax, matchesStructure]);

  const archivedCount = useMemo(() => data.prest.filter((p) => p.archivedAt).length, [data.prest]);

  const resetAdvanced = () => {
    setDateFrom(''); setDateTo(''); setDomaine(''); setService('');
    setArtiste(''); setLieu(''); setMontantMin(''); setMontantMax('');
  };

  const advancedActive = !!(dateFrom || dateTo || domaine || service || artiste || lieu || montantMin || montantMax);

  const progressPct = (statut: string) => {
    if (statut === 'Signé') return 80;
    if (/Contrat/i.test(statut)) return 70;
    if (/Propal trans/i.test(statut)) return 50;
    if (/Propal A FAIRE/i.test(statut)) return 35;
    if (/Relance/i.test(statut)) return 65;
    if (/Demande/i.test(statut)) return 25;
    return 10;
  };
  const progressColor = (statut: string) => {
    if (statut === 'Signé') return 'bg-emerald-500';
    if (/Refus/i.test(statut)) return 'bg-red-500';
    if (/Relance/i.test(statut)) return 'bg-sky-500';
    if (/Propal/i.test(statut)) return 'bg-violet-500';
    return 'bg-amber-500';
  };

  const [exporting, setExporting] = useState(false);
  const handleExport = async () => {
    if (exporting) return;
    setExporting(true);
    const tId = toast.loading('Génération du fichier Excel…');
    try {
      const stamp = new Date().toISOString().slice(0, 10);
      await Promise.resolve(exportXlsx(`prestations-${stamp}.xlsx`, filtered, [
        { header: 'N°', key: 'numero', width: 14 },
        { header: 'Statut', key: 'statut', width: 22 },
        { header: 'Client', key: 'clientNom' as any, width: 28 },
        { header: 'Date événement', key: 'dateEvent' as any, format: 'date', width: 14 },
        { header: 'Lieu', key: 'lieu' as any, width: 32 },
        { header: 'Domaines', key: (r: any) => (r.domaines || []).join(', '), width: 24 },
        { header: 'Services', key: (r: any) => (r.services || []).join(', '), width: 24 },
        { header: 'Artistes', key: 'artistes' as any, width: 28 },
        { header: 'Prop. TTC', key: 'propTTC' as any, format: 'currency', width: 14 },
        { header: 'Devis', key: 'devis' as any, format: 'currency', width: 14 },
        { header: 'Acompte %', key: 'acomptePct' as any, format: 'number', width: 10 },
        { header: 'Demande', key: 'demande' as any, width: 40 },
        { header: 'Structure', key: 'structure' as any, width: 16 },
        { header: 'Créé le', key: 'dateCreation' as any, format: 'date', width: 14 },
      ], 'Prestations'));
      toast.success(`✓ Export Excel généré (${filtered.length} ligne${filtered.length > 1 ? 's' : ''})`, { id: tId });
    } catch (e: any) {
      toast.error('Échec de l’export : ' + (e?.message || 'inconnu'), { id: tId });
    } finally {
      setExporting(false);
    }
  };

  return (
    <div>
      <PageTitle title="Suivi Prestations" subtitle={`${filtered.length} dossiers affichés${conflits.size ? ` · ⚠️ ${conflits.size} date(s) en conflit` : ''}`} action={
        <div className="flex items-center gap-2">
          <button onClick={handleExport} disabled={exporting} title="Exporter en Excel" className="px-3 py-2.5 bg-emerald-500/15 hover:bg-emerald-500/25 disabled:opacity-60 disabled:cursor-wait text-emerald-300 border border-emerald-500/40 font-medium rounded-lg text-sm transition-colors flex items-center gap-2">
            <FileSpreadsheet size={14} strokeWidth={1.5} className={exporting ? 'animate-pulse' : ''} /> {exporting ? 'Export…' : 'Excel'}
          </button>
          <Link to="../nouvelle-demande" className="px-4 py-2.5 bg-amber-500/15 hover:bg-amber-500/25 text-amber-300 border border-amber-500/40 font-medium rounded-lg text-sm transition-colors flex items-center gap-2">
            <Sparkles size={14} strokeWidth={1.5} /> Nouvelle demande (assistant)
          </Link>
          <button onClick={openNew} className="px-4 py-2.5 bg-amber-500 hover:bg-amber-400 text-slate-950 font-medium rounded-lg text-sm transition-colors">+ Ajout rapide</button>
        </div>
      } />

      <PrestationForm open={openForm} onOpenChange={setOpenForm} initial={editing} />
      <IntervenantsModal open={!!intervPrest} onOpenChange={(v) => { if (!v) setIntervPrest(null); }} prestNumero={intervPrest?.id || null} clientNom={intervPrest?.client} />

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="relative flex-1 min-w-[280px] max-w-md">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" strokeWidth={1.5} />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="🔍 Rechercher..."
            className="w-full pl-9 pr-3 py-2 bg-slate-900/60 border border-slate-800 rounded-lg text-sm text-slate-200 placeholder-slate-500 focus:outline-none focus:border-amber-500/50"
          />
        </div>
        <div className="flex gap-1.5 flex-wrap">
          {FILTERS.map((f) => (
            <button
              key={f.key}
              onClick={() => setFilter(f.key)}
              className={`px-3 py-2 rounded-lg text-xs font-medium border transition-colors ${
                filter === f.key ? 'bg-amber-500/15 text-amber-400 border-amber-500/40' : 'bg-slate-900/60 text-slate-400 border-slate-800 hover:bg-slate-800'
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
        <button
          onClick={() => setShowAdvanced((v) => !v)}
          className={`px-3 py-2 rounded-lg text-xs font-medium border ${
            advancedActive || showAdvanced ? 'bg-sky-500/15 text-sky-400 border-sky-500/40' : 'bg-slate-900/60 text-slate-400 border-slate-800 hover:bg-slate-800'
          }`}
        >
          ⚙️ Filtres avancés{advancedActive ? ' •' : ''}
        </button>
        <button
          onClick={() => setShowArchived((v) => !v)}
          title={showArchived ? 'Masquer les archives' : 'Afficher les archives'}
          className={`px-3 py-2 rounded-lg text-xs font-medium border ${
            showArchived ? 'bg-amber-500/15 text-amber-400 border-amber-500/40' : 'bg-slate-900/60 text-slate-400 border-slate-800 hover:bg-slate-800'
          }`}
        >
          📦 {showArchived ? 'Archives affichées' : `Afficher archives (${archivedCount})`}
        </button>
      </div>

      {/* Filtres avancés */}
      {showAdvanced && (
        <Card className="p-4 mb-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div>
              <label className="text-[10px] text-slate-400 uppercase tracking-wider block mb-1">Du</label>
              <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="w-full px-2 py-1.5 bg-slate-900 border border-slate-800 rounded text-xs text-slate-200" />
            </div>
            <div>
              <label className="text-[10px] text-slate-400 uppercase tracking-wider block mb-1">Au</label>
              <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="w-full px-2 py-1.5 bg-slate-900 border border-slate-800 rounded text-xs text-slate-200" />
            </div>
            <div>
              <label className="text-[10px] text-slate-400 uppercase tracking-wider block mb-1">Domaine</label>
              <select value={domaine} onChange={(e) => setDomaine(e.target.value)} className="w-full px-2 py-1.5 bg-slate-900 border border-slate-800 rounded text-xs text-slate-200">
                <option value="">— Tous —</option>
                {allDomaines.map((d) => <option key={d} value={d}>{d}</option>)}
              </select>
            </div>
            <div>
              <label className="text-[10px] text-slate-400 uppercase tracking-wider block mb-1">Service</label>
              <select value={service} onChange={(e) => setService(e.target.value)} className="w-full px-2 py-1.5 bg-slate-900 border border-slate-800 rounded text-xs text-slate-200">
                <option value="">— Tous —</option>
                {allServices.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div>
              <label className="text-[10px] text-slate-400 uppercase tracking-wider block mb-1">Artiste</label>
              <select value={artiste} onChange={(e) => setArtiste(e.target.value)} className="w-full px-2 py-1.5 bg-slate-900 border border-slate-800 rounded text-xs text-slate-200">
                <option value="">— Tous —</option>
                {allArtistes.map((a) => <option key={a} value={a}>{a}</option>)}
              </select>
            </div>
            <div>
              <label className="text-[10px] text-slate-400 uppercase tracking-wider block mb-1">Lieu (contient)</label>
              <input value={lieu} onChange={(e) => setLieu(e.target.value)} placeholder="Filtrer par ville…" className="w-full px-2 py-1.5 bg-slate-900 border border-slate-800 rounded text-xs text-slate-200" />
            </div>
            <div>
              <label className="text-[10px] text-slate-400 uppercase tracking-wider block mb-1">Montant min (€)</label>
              <input type="number" value={montantMin} onChange={(e) => setMontantMin(e.target.value)} className="w-full px-2 py-1.5 bg-slate-900 border border-slate-800 rounded text-xs text-slate-200" />
            </div>
            <div>
              <label className="text-[10px] text-slate-400 uppercase tracking-wider block mb-1">Montant max (€)</label>
              <input type="number" value={montantMax} onChange={(e) => setMontantMax(e.target.value)} className="w-full px-2 py-1.5 bg-slate-900 border border-slate-800 rounded text-xs text-slate-200" />
            </div>
          </div>
          {advancedActive && (
            <button onClick={resetAdvanced} className="mt-3 text-xs text-slate-400 hover:text-slate-200 inline-flex items-center gap-1">
              <X size={12} /> Réinitialiser les filtres
            </button>
          )}
        </Card>
      )}

      <div className="flex gap-1.5 mb-5 flex-wrap">
        <button onClick={() => setYear('all')} className={`px-3 py-1.5 rounded-md text-xs border ${year === 'all' ? 'bg-sky-500/15 text-sky-400 border-sky-500/40' : 'bg-slate-900/60 text-slate-400 border-slate-800'}`}>Toutes années</button>
        {years.map((y) => (
          <button key={y} onClick={() => setYear(y)} className={`px-3 py-1.5 rounded-md text-xs border ${year === y ? 'bg-sky-500/15 text-sky-400 border-sky-500/40' : 'bg-slate-900/60 text-slate-400 border-slate-800'}`}>{y}</button>
        ))}
      </div>

      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-xs table-auto">
            <thead className="bg-slate-900/80 text-[9px] text-slate-400 uppercase tracking-wider">
              <tr>
                <th className="px-1 py-2 text-left">●</th>
                <th className="px-1 py-2 text-left">Dossier</th>
                <th className="px-1 py-2 text-left">Suivi</th>
                <th className="px-1 py-2 text-left">Client</th>
                <th className="px-1 py-2 text-left">Date</th>
                <th className="px-1 py-2 text-left">Lieu</th>
                <th className="px-1 py-2 text-left">KM/Durée</th>
                <th className="px-1 py-2 text-left">Statut</th>
                <th className="px-1 py-2 text-left">Services</th>
                <th className="px-1 py-2 text-left">Domaines</th>
                <th className="px-1 py-2 text-left" title="Tarif artiste HT / Tarif client HT / Gain">Artistes (Tarif / CY / Gain)</th>
                <th className="px-1 py-2 text-right">Prop. HT</th>
                <th className="px-1 py-2 text-right">Coût artistes HT</th>
                <th className="px-1 py-2 text-right">Gain HT</th>
                <th className="px-1 py-2 text-left">Activité</th>
                <th className="px-1 py-2 text-left">Progression</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((p) => {
                const dot =
                  p.statut === 'Signé' ? 'bg-emerald-500' :
                  /Refus/i.test(p.statut) ? 'bg-red-500' :
                  /Relance/i.test(p.statut) ? 'bg-amber-500' : 'bg-sky-500';
                const dCreate = daysUntil(p.dateCreation);
                const enConflit = p.dateEvent && conflits.has(p.dateEvent) && !/Refus/i.test(p.statut);
                // Alertes visuelles dates (inspirées de votre Excel)
                const dEvent = p.dateEvent ? daysUntil(p.dateEvent) : null;
                const isClos = /Refus|Réalis|Annul/i.test(p.statut);
                const eventRetard = dEvent != null && dEvent < 0 && !isClos;
                const eventBientot = dEvent != null && dEvent >= 0 && dEvent <= 30 && !isClos;
                const rowAlert = eventRetard ? 'bg-red-500/10 border-l-2 border-l-red-500'
                  : eventBientot ? 'bg-amber-500/5 border-l-2 border-l-amber-500'
                  : '';
                return (
                  <tr key={p.id} className={`border-t border-slate-800/60 hover:bg-slate-800/30 transition-colors align-top ${enConflit ? 'bg-rose-500/5' : ''} ${rowAlert}`}>
                    <td className="px-1.5 py-2"><span className={`inline-block w-2 h-2 rounded-full ${dot}`} /></td>
                    <td className="px-1.5 py-2 text-[10px] text-slate-400 whitespace-nowrap">
                      {formatDateFR(p.dateCreation, { day: '2-digit', month: '2-digit', year: '2-digit' })}<br />
                      <span className="text-[9px] text-emerald-400">{dCreate != null ? `J${dCreate >= 0 ? '-' : '+'}${Math.abs(dCreate)}` : ''}</span>
                    </td>
                    <td className="px-1.5 py-2">
                      <div className="flex flex-col gap-1">
                        <button onClick={() => openEdit(p)} className="px-1.5 py-0.5 bg-slate-800 hover:bg-slate-700 rounded text-[9px] text-slate-300 inline-flex items-center gap-1"><Pencil size={9} strokeWidth={1.5} /> Éditer</button>
                        {canAssign && (
                          <button onClick={() => setIntervPrest(p)} className="px-1.5 py-0.5 bg-amber-500/10 hover:bg-amber-500/20 border border-amber-500/30 rounded text-[9px] text-amber-300 inline-flex items-center gap-1" title="Attribuer / modifier les intervenants"><Users2 size={9} strokeWidth={1.5} /> Intervenants</button>
                        )}
                        <button
                          onClick={async () => {
                            const ok = await confirm({
                              title: 'Supprimer la prestation ?',
                              description: `« ${p.client} » du ${formatDateFR(p.dateEvent)}\n\nElle sera déplacée dans la Corbeille et pourra être restaurée.`,
                              confirmLabel: 'Supprimer',
                              destructive: true,
                            });
                            if (ok) crmStore.removePrest(p.id);
                          }}
                          className="px-1.5 py-0.5 bg-rose-500/10 hover:bg-rose-500/20 border border-rose-500/30 rounded text-[9px] text-rose-300 inline-flex items-center gap-1"
                          title="Déplacer vers la Corbeille"
                        >
                          <Trash2 size={9} strokeWidth={1.5} /> Suppr.
                        </button>
                      </div>
                    </td>
                    <td className="px-1.5 py-2 overflow-hidden">
                      <div className="flex items-center gap-1 mb-0.5"><StructureBadge code={p.structure} /></div>
                      <div className="font-medium text-slate-200 text-xs leading-tight break-words">{p.client}</div>
                      <div className="text-[10px] text-slate-500 mt-0.5 leading-tight break-words">{(p.themes || []).join(', ') || p.libelle} · {p.demande}</div>
                    </td>
                    <td className="px-1.5 py-2 whitespace-nowrap">
                      <div className="text-amber-400 text-[11px] font-medium flex items-center gap-1">
                        {formatDateFR(p.dateEvent, { day: '2-digit', month: '2-digit', year: '2-digit' })}
                        {enConflit && (
                          <span title="Conflit : autre(s) prestation(s) le même jour" className="inline-flex items-center gap-0.5 px-1 py-0.5 bg-rose-500/15 text-rose-400 rounded text-[8px] border border-rose-500/30">
                            <AlertTriangle size={8} strokeWidth={2} />
                          </span>
                        )}
                      </div>
                      <div className="text-[9px] text-amber-300">{jLabel(p.dateEvent)}</div>
                    </td>
                    <td className="px-1.5 py-2 text-[11px] text-slate-300">
                      {p.lieu ? (() => {
                        const { ville, cpDept } = parseLieu(p.lieu);
                        return (
                          <div className="relative group/lieu" title={p.lieu}>
                            <div className="font-medium text-slate-200 leading-tight truncate">{ville || p.lieu}</div>
                            {cpDept && <div className="text-[9px] text-slate-500 leading-tight">{cpDept}</div>}
                            <div className="pointer-events-none absolute left-0 top-full mt-1 z-50 hidden group-hover/lieu:block whitespace-nowrap px-2 py-1 bg-slate-800 border border-slate-700 rounded text-[11px] text-slate-100 shadow-lg max-w-xs">
                              {p.lieu}
                            </div>
                          </div>
                        );
                      })() : '—'}
                    </td>
                    <td className="px-1.5 py-2 text-[10px] whitespace-nowrap">
                      {p.km != null && (
                        <div className="inline-flex items-center gap-1 px-1 py-0.5 bg-rose-500/10 text-rose-400 rounded text-[9px]">
                          <Truck size={9} strokeWidth={1.5} /> {p.km}km
                        </div>
                      )}
                      <div className="text-slate-400 text-[9px] mt-0.5">{p.duree}</div>
                    </td>
                    <td className="px-1.5 py-2"><StatutInlineSelect p={p} /></td>
                    <td className="px-1.5 py-2"><div className="flex flex-wrap gap-0.5">{p.services.map((s) => <Tag key={s} color="emerald">{s}</Tag>)}</div></td>
                    <td className="px-1.5 py-2"><div className="flex flex-wrap gap-0.5">{p.domaines.map((s) => <Tag key={s} color="violet">{s}</Tag>)}</div></td>
                    <td className="px-1.5 py-2 text-[10px] text-slate-300 leading-tight">
                      {p.artistesDetails && p.artistesDetails.length > 0 ? (
                        <div className="flex flex-col gap-1">
                          {p.artistesDetails.map((a, i) => {
                            const gain = a.propTtc != null && a.cout != null ? a.propTtc - a.cout : null;
                            return (
                              <div key={i} className="leading-tight">
                                <div className="font-medium text-slate-200">{a.nom}</div>
                                <div className="text-[9px] flex flex-wrap gap-x-1.5">
                                  <span className="text-rose-300" title="Tarif artiste HT">{a.cout != null ? formatEUR(a.cout) : '—'}</span>
                                  <span className="text-slate-600">/</span>
                                  <span className="text-amber-300" title="Tarif client HT">{a.propTtc != null ? formatEUR(a.propTtc) : '—'}</span>
                                  <span className="text-slate-600">/</span>
                                  <span className={gain == null ? 'text-slate-500' : gain >= 0 ? 'text-emerald-400' : 'text-rose-400'} title="Gain">{gain != null ? formatEUR(gain) : '—'}</span>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      ) : (
                        <span className="break-words">{p.artistes.join(', ') || '—'}</span>
                      )}
                    </td>
                    <td className="px-1.5 py-2 text-right text-amber-400 text-[11px] whitespace-nowrap">{p.propTTC != null ? formatEUR(p.propTTC) : '—'}</td>
                    <td className="px-1.5 py-2 text-right text-rose-300 text-[11px] whitespace-nowrap">{p.coutArtistes != null ? formatEUR(p.coutArtistes) : '—'}</td>
                    <td className="px-1.5 py-2 text-right text-[11px] whitespace-nowrap">
                      {p.propTTC != null && p.coutArtistes != null ? (() => {
                        const gain = p.propTTC - p.coutArtistes;
                        const cls = gain >= 0 ? 'text-emerald-400' : 'text-rose-400';
                        return <span className={cls}>{formatEUR(gain)}</span>;
                      })() : <span className="text-slate-500">—</span>}
                    </td>
                    <td className="px-1.5 py-2 whitespace-nowrap">
                      {(() => {
                        const d = daysSinceUpdate(p);
                        if (d == null) return <span className="text-[9px] text-slate-500">—</span>;
                        const cls = d <= 7 ? 'text-emerald-400 bg-emerald-500/10' : d <= 15 ? 'text-amber-400 bg-amber-500/10' : 'text-rose-400 bg-rose-500/10';
                        return (
                          <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] ${cls}`} title={`Dernière action il y a ${d} jour(s)`}>
                            <Activity size={9} strokeWidth={1.5} /> J+{d}
                          </span>
                        );
                      })()}
                    </td>
                    <td className="px-1.5 py-2">
                      <div className="h-1 bg-slate-800 rounded-full overflow-hidden">
                        <div className={`h-full ${progressColor(p.statut)} rounded-full`} style={{ width: `${progressPct(p.statut)}%` }} />
                      </div>
                      <div className="text-[9px] text-slate-500 mt-0.5 text-right">{progressPct(p.statut)}%</div>
                    </td>
                  </tr>
                );
              })}
              {!filtered.length && (
                <tr><td colSpan={16} className="text-center py-12 text-slate-500 text-sm">Aucune prestation trouvée.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
